import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { keyframes } from '@emotion/react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import BackButton from 'src/components/BackButton';
import {
  COMPACT_ROW_ID_SX,
  COMPACT_ROW_LINK_SX,
  COMPACT_ROW_META_SX,
  COMPACT_ROW_NAME_SX,
  COMPACT_ROW_SX,
  OUTLINED_ROW_DIVIDER,
  OutlinedRowList,
  OutlinedRowListSkeleton,
  RowCheckbox,
} from 'src/components/CompactRowList';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import SuccessButton from 'src/components/SuccessButton';
import WarningIcon from 'src/components/WarningIcon';
import { USER_QUERY_KEY } from 'src/constants/query';
import {
  StatusDialog,
  StatusFormLayout,
  UPDATE_TRUST_STATUS_ICON,
  UPDATE_TRUST_STATUS_TITLE,
  statusSchema,
} from 'src/features/appStatus/UpdateStatusModal';
import { buildDefaultStatusNote } from 'src/features/appStatus/defaultStatusNote';
import ManagedBasePolicyField from 'src/features/basePolicy/ManagedBasePolicyField';
import ModalSectionCard, { ModalSectionBullet } from 'src/features/basePolicy/ModalSectionCard';
import UseExistingPolicyModal from 'src/features/basePolicy/UseExistingPolicyModal';
import CreateManagedBasePolicyModal from 'src/features/basePolicy/CreateManagedBasePolicyModal';
import { useManagedBasePolicyOptions } from 'src/hooks/environmentWdacPolicy';
import { useWin32AppFileSets, useWin32AppPolicy, useWin32AppWdacFileCounts } from 'src/hooks/wdac';
import useQueryData from 'src/queries/useQueryData';
import { userEmailSelector } from 'src/selectors/user';
import {
  APPLICATION_TRUST_STATUS,
  getApplicationTrustStatusOptions,
} from 'src/utils/enums/applicationTrustStatus';
import { isCustomerEnvironmentApplicationEnabled } from 'src/utils/enums/customerEnvironmentApplicationStatus';
import { TELEMETRY_REPORTED_WINDOW } from 'src/utils/enums/telemetryReportedWindow';
import { formatCount } from 'src/utils/formatNumber.util';
import { getNextPolicyVersion } from 'src/utils/policyVersion';
import { getWin32StatusNote } from './constants';
import UncoveredFilesModal from './UncoveredFilesModal';
import Win32FileSetModal from './Win32FileSetModal';
import { FileSetImportRow, formatFileCountMeta } from './Win32FileSetImports';

const STATUS_OPTIONS = getApplicationTrustStatusOptions();

const EMPTY_VALUES = {
  status: '',
  note: '',
  fileSetChoices: {},
  createImport: false,
  basePolicyChoices: {},
};

const CLOSED_VIEW = { kind: null, fileSetId: null };

// Setting the status comes first; trusting continues to the artifacts the trust is built into.
const STATUS_STEP = 'status';
const ARTIFACTS_STEP = 'artifacts';

// Without a scope from the telemetry tab, the import offered is everything uncovered lately.
const DEFAULT_IMPORT_SCOPE = { reportedWindow: TELEMETRY_REPORTED_WINDOW.Last90Days };

const NOTHING_SELECTED_WARNING =
  'Nothing is selected, so trusting this app covers none of its files and creates no security catalog / supplemental policy';

const FILE_SETS_HELP =
  'Each file set import is a set of files identified as part of this app. Checked imports are what its security catalog allows to run. Click a hash to see what an import holds, or create an import from the files devices reported that no import covers yet.';

const BASE_POLICIES_HELP =
  'The catalog is generated once and attached to a supplemental policy for each checked base policy, all versioned together. Each base policy must allow catalog signing.';

const BASE_POLICIES_DISABLED_REASON =
  'Select a file set import first. Base policies only apply when there is a catalog to generate.';

const HELP_SX = { color: '#BDBDBD', fontSize: '0.76rem' };

// Twice, once the dialog has faded in, to point at the import the telemetry tab opened this for.
const HIGHLIGHT_BLINK = keyframes`
  0%, 100% { background-color: transparent; }
  50% { background-color: rgba(135, 200, 224, 0.22); }
`;

// The form keeps only the choices made in it, keyed by id, so a box reads straight from the list it
// is drawn from and falls back to how that row is saved until it is touched.
const isChosen = (choices, id, saved) => choices[id] ?? saved;

const toggleChoice = (choices, id, checked) => ({ ...choices, [id]: !checked });

export const getStatusSavedMessage = ({ import: importScope, generatesCatalog }) =>
  [
    'Status updated',
    importScope ? 'WDAC import created' : null,
    generatesCatalog ? 'catalog generation started' : null,
  ]
    .filter(Boolean)
    .join(', ');

// The one row that makes an import rather than describing one, marked out by its icon. A count
// still loading leaves it undefined. Its title opens the files the import would take, where there
// is a list of them to show.
const CreateImportRow = ({ control, title, fileCount, onOpen, highlight }) => {
  const canImport = fileCount > 0;
  const disabledReason =
    fileCount === undefined
      ? 'Counting the files not covered by the app...'
      : fileCount === 0
        ? 'Every one of these files is already covered by the app.'
        : '';

  return (
    <Controller
      name="createImport"
      control={control}
      render={({ field: { value, onChange } }) => {
        const checked = canImport && !!value;

        return (
          <Stack
            direction="row"
            gap={1.25}
            alignItems="center"
            onClick={canImport ? () => onChange(!checked) : undefined}
            sx={{
              ...COMPACT_ROW_SX,
              cursor: canImport ? 'pointer' : 'default',
              ...(highlight ? { animation: `${HIGHLIGHT_BLINK} 0.6s ease-in-out 0.35s 2` } : null),
            }}>
            <Box display="inline-flex" sx={{ flexShrink: 0 }}>
              <Iconify icon="basil:add-outline" width={20} height={20} color="#87C8E0" />
            </Box>
            <Typography sx={COMPACT_ROW_NAME_SX}>Create WDAC Import</Typography>
            <Typography
              onClick={
                canImport && onOpen
                  ? event => {
                      event.stopPropagation();
                      onOpen();
                    }
                  : undefined
              }
              sx={{
                ...COMPACT_ROW_ID_SX,
                fontFamily: 'inherit',
                color: '#9CA3AF',
                ...(canImport && onOpen ? COMPACT_ROW_LINK_SX : null),
              }}>
              {title}
            </Typography>
            <Typography sx={COMPACT_ROW_META_SX}>
              {fileCount === undefined
                ? 'Counting files...'
                : formatFileCountMeta(fileCount, new Date())}
            </Typography>
            <Tooltip title={disabledReason}>
              {/* A disabled checkbox fires no events, so the tooltip hangs off a wrapper. */}
              <Stack direction="row" alignItems="center" gap={1.25} sx={{ alignSelf: 'stretch' }}>
                <RowCheckbox
                  checked={checked}
                  disabled={!canImport}
                  onChange={event => onChange(event.target.checked)}
                  label="Create a WDAC import for these files"
                />
              </Stack>
            </Tooltip>
          </Stack>
        );
      }}
    />
  );
};

const FileSetSection = ({
  control,
  fileSets,
  nothingSelected,
  importTitle,
  importFileCount,
  highlightImport,
  onOpenFileSet,
  onOpenImportFiles,
}) => (
  <OutlinedRowList
    label="File Set Imports"
    adornment={
      <WarningIcon
        message={nothingSelected ? NOTHING_SELECTED_WARNING : null}
        width={13}
        height={13}
      />
    }>
    {!fileSets ? (
      <OutlinedRowListSkeleton />
    ) : (
      <Controller
        name="fileSetChoices"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Stack divider={OUTLINED_ROW_DIVIDER}>
            {fileSets.map(row => {
              const id = row.customerEnvironmentApplicationId;
              const checked = isChosen(
                value,
                id,
                isCustomerEnvironmentApplicationEnabled(row.status),
              );
              const toggle = () => onChange(toggleChoice(value, id, checked));

              return (
                <FileSetImportRow
                  key={id}
                  row={row}
                  onClick={toggle}
                  onHashClick={() => onOpenFileSet(row.fileSetId)}
                  muted={!checked}
                  trailing={
                    <RowCheckbox
                      checked={checked}
                      onChange={toggle}
                      label="Enable this file set import"
                    />
                  }
                />
              );
            })}
            <CreateImportRow
              control={control}
              title={importTitle}
              fileCount={importFileCount}
              onOpen={onOpenImportFiles}
              highlight={highlightImport}
            />
          </Stack>
        )}
      />
    )}
  </OutlinedRowList>
);

// The base policies the catalog is versioned for. They only mean something with a catalog to
// generate, so until an import is selected they stay as saved and cannot be changed.
const BasePolicySection = ({
  control,
  basePolicies,
  savedBaseIds,
  disabled,
  missingBase,
  ...links
}) => (
  <Controller
    name="basePolicyChoices"
    control={control}
    render={({ field: { value, onChange } }) => (
      <ManagedBasePolicyField
        basePolicies={basePolicies}
        loading={!savedBaseIds}
        isSelected={policyId => isChosen(value, policyId, savedBaseIds?.has(policyId) ?? false)}
        onToggle={(policyId, checked) => onChange(toggleChoice(value, policyId, checked))}
        disabled={disabled}
        disabledReason={BASE_POLICIES_DISABLED_REASON}
        error={missingBase ? 'Select at least one base policy to generate the catalog.' : null}
        {...links}
      />
    )}
  />
);

// The one place a single Win32 app is trusted. The first step sets the status and note, which is all
// any other status needs. Trusting continues to building its artifacts: the imports the app is made
// of, an import from what devices reported outside them, and the catalog versioned for the chosen
// base policies, all saved together with the trust. Opened from the telemetry tab, or from what the
// general tab shows of the artifacts, it starts on that second step. Opening an import, the files an
// import would take or a base policy form stands in for this one until it is closed, and the form
// keeps what was entered meanwhile.
const Win32AppStatusModal = ({
  open,
  onClose,
  onSave,
  loading,
  globalApplicationVersionId,
  summary,
  currentStatus,
  currentDate,
  importScope,
  startOnArtifacts = false,
}) => {
  const email = useQueryData(USER_QUERY_KEY, userEmailSelector);
  const [view, setView] = useState(CLOSED_VIEW);
  const [step, setStep] = useState(STATUS_STEP);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: EMPTY_VALUES,
    resolver: yupResolver(statusSchema),
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { isDirty, isValid },
  } = formMethods;
  const [status, fileSetChoices, createImport, basePolicyChoices] = watch([
    'status',
    'fileSetChoices',
    'createImport',
    'basePolicyChoices',
  ]);
  const isTrusted = status === APPLICATION_TRUST_STATUS.Trusted;
  const scope = importScope || DEFAULT_IMPORT_SCOPE;

  const { data: fileSets } = useWin32AppFileSets({ globalApplicationVersionId, enabled: open });
  // Saving the status moves its date to now, so the default offer counts the last 90 days of
  // reports, which is also what the server imports. A telemetry scope brings its own count.
  const { data: counts } = useWin32AppWdacFileCounts({
    globalApplicationVersionId,
    reportedWindow: DEFAULT_IMPORT_SCOPE.reportedWindow,
    enabled: open && !importScope,
  });
  const { data: policyState } = useWin32AppPolicy({ globalApplicationVersionId, enabled: open });
  const { data: basePolicyOptions } = useManagedBasePolicyOptions({ enabled: open });

  const basePolicies = useMemo(() => basePolicyOptions?.policies || [], [basePolicyOptions]);
  const activeBaseIds = useMemo(
    () =>
      (policyState?.policies || [])
        .filter(policy => policy.isActive)
        .map(policy => policy.basePolicyId),
    [policyState],
  );
  // The catalog keeps the bases it already covers; a first one starts from the environment defaults.
  const savedBaseIds = useMemo(
    () =>
      basePolicyOptions && policyState
        ? new Set(
            activeBaseIds.length
              ? activeBaseIds
              : basePolicies
                  .filter(policy => policy.environmentDefault)
                  .map(policy => policy.policyId),
          )
        : null,
    [basePolicyOptions, policyState, activeBaseIds, basePolicies],
  );

  // Each opening starts from what is saved, with a note naming who made this change. Opening on the
  // artifacts step means trusting, and a telemetry scope arrives with its import already chosen.
  useEffect(() => {
    if (open) {
      const onArtifacts = !!importScope || startOnArtifacts;

      reset({
        ...EMPTY_VALUES,
        status: onArtifacts ? APPLICATION_TRUST_STATUS.Trusted : currentStatus || '',
        note: buildDefaultStatusNote(email),
        createImport: !!importScope,
      });
      setView(CLOSED_VIEW);
      setStep(onArtifacts ? ARTIFACTS_STEP : STATUS_STEP);
    }
  }, [open, importScope, startOnArtifacts, currentStatus, email, reset]);

  const importFileCount = importScope ? importScope.fileCount : counts?.uncoveredFileCount;
  const importing = isTrusted && createImport && importFileCount > 0;
  const enabledFileSetIds = (fileSets || [])
    .filter(fileSet =>
      isChosen(
        fileSetChoices,
        fileSet.customerEnvironmentApplicationId,
        isCustomerEnvironmentApplicationEnabled(fileSet.status),
      ),
    )
    .map(fileSet => fileSet.customerEnvironmentApplicationId);
  const selectedBaseIds = savedBaseIds
    ? basePolicies
        .filter(policy =>
          isChosen(basePolicyChoices, policy.policyId, savedBaseIds.has(policy.policyId)),
        )
        .map(policy => policy.policyId)
    : [];

  const generatesCatalog = isTrusted && (enabledFileSetIds.length > 0 || importing);
  const missingBase = generatesCatalog && !!savedBaseIds && selectedBaseIds.length === 0;
  const coveredImportCount = enabledFileSetIds.length + (importing ? 1 : 0);
  const nextVersion = activeBaseIds.length
    ? getNextPolicyVersion(policyState?.currentVersion)
    : '1.0.0.0';
  const artifactsReady = !!fileSets && !!savedBaseIds;

  const importTitle = !importScope
    ? 'Uncovered Files in Last 90 days'
    : importScope.fileIds
      ? 'Selected Telemetry Files'
      : 'Telemetry Files Matching Your Filters';

  // The ids are kept on close so the file set modal does not empty out mid transition.
  const closeView = () => setView(previous => ({ ...previous, kind: null }));
  const openView = kind => () => setView(previous => ({ ...previous, kind }));

  // The base policy just brought under management is the one asked for, so it comes back selected.
  const selectBasePolicy = policy => {
    setValue(
      'basePolicyChoices',
      { ...getValues('basePolicyChoices'), [policy.policyId]: true },
      { shouldDirty: true },
    );
    closeView();
  };

  // Any status but Trusted is the status and note alone. Nulls leave the app's imports and catalog
  // as they are.
  const saveStatusOnly = values =>
    onSave({
      status: values.status,
      notes: values.note?.trim() || '',
      enabledCustomerEnvironmentApplicationIds: null,
      import: null,
      basePolicyIds: null,
      generatesCatalog: false,
    });

  const saveTrust = values =>
    onSave({
      status: values.status,
      notes: values.note?.trim() || '',
      enabledCustomerEnvironmentApplicationIds: enabledFileSetIds,
      import: importing
        ? {
            reportedWindow: scope.reportedWindow,
            isSigned: scope.isSigned ?? null,
            search: scope.search || null,
            fileIds: scope.fileIds ?? null,
          }
        : null,
      basePolicyIds: generatesCatalog ? selectedBaseIds : null,
      generatesCatalog,
    });

  const onArtifactsStep = step === ARTIFACTS_STEP;

  return (
    <>
      <StatusDialog
        open={open && !view.kind}
        onClose={onClose}
        title={onArtifactsStep ? 'Build Trusted Artifacts' : UPDATE_TRUST_STATUS_TITLE}
        icon={onArtifactsStep ? 'mdi:package-variant-closed' : UPDATE_TRUST_STATUS_ICON}
        summary={summary}
        currentDate={currentDate}
        lastGeneratedDate={onArtifactsStep ? policyState?.lastGeneratedAt : null}
        maxWidth={onArtifactsStep ? 'md' : 'sm'}>
        {!onArtifactsStep ? (
          <StatusFormLayout
            formMethods={formMethods}
            statusOptions={STATUS_OPTIONS}
            infoAlert={getWin32StatusNote}
            // Trusting continues even unchanged, since the next step is where the work is.
            canSubmit={isTrusted || isDirty}
            submitLabel={isTrusted ? 'Continue' : 'Save'}
            loading={loading}
            onCancel={onClose}
            onSubmit={values => (isTrusted ? setStep(ARTIFACTS_STEP) : saveStatusOnly(values))}>
            {isTrusted ? (
              <Typography sx={HELP_SX}>
                Continue to choose which files this trust covers and build the app&apos;s security
                catalog.
              </Typography>
            ) : null}
          </StatusFormLayout>
        ) : (
          <form autoComplete="off" onSubmit={handleSubmit(saveTrust)}>
            <Stack spacing={2.5}>
              <ModalSectionCard icon="mdi:playlist-check" title="This will">
                <ModalSectionBullet>
                  Mark this application{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Trusted
                  </Box>{' '}
                  in your environment
                </ModalSectionBullet>
                {importing ? (
                  <ModalSectionBullet tooltip="The files become a new file set import on the app, enabled straight away, with a note saying when it was added.">
                    {`Create a WDAC import of ${formatCount(importFileCount)} ${
                      importFileCount === 1 ? 'file' : 'files'
                    } in your App Import Catalog`}
                  </ModalSectionBullet>
                ) : null}
                {generatesCatalog ? (
                  <ModalSectionBullet tooltip="You still deploy the supplemental policies to your devices before they allow the app.">
                    Generate security catalog version{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {nextVersion}
                    </Box>
                    {` from ${formatCount(coveredImportCount)} file set ${
                      coveredImportCount === 1 ? 'import' : 'imports'
                    } for ${formatCount(selectedBaseIds.length)} base ${
                      selectedBaseIds.length === 1 ? 'policy' : 'policies'
                    }`}
                  </ModalSectionBullet>
                ) : (
                  <ModalSectionBullet>
                    Generate no security catalog, since no file set imports are selected
                  </ModalSectionBullet>
                )}
              </ModalSectionCard>

              {/* Opened from the telemetry tab, the import is already the one this points to. */}
              {importScope ? null : (
                <InfoAlert sx={{ py: 0.25 }}>
                  <Typography variant="caption">
                    You can also create a WDAC import from an application&apos;s Telemetry tab,
                    picking from the files seen across your devices with filters and search, rather
                    than taking every uncovered file at once.
                  </Typography>
                </InfoAlert>
              )}

              <Stack gap={1}>
                <Typography sx={HELP_SX}>{FILE_SETS_HELP}</Typography>
                <FileSetSection
                  control={control}
                  fileSets={fileSets}
                  nothingSelected={!!fileSets && !generatesCatalog}
                  importTitle={importTitle}
                  importFileCount={importFileCount}
                  highlightImport={!!importScope}
                  onOpenFileSet={fileSetId => setView({ kind: 'fileSet', fileSetId })}
                  // A hand-picked selection has no list to show beyond the one it was picked from.
                  onOpenImportFiles={importScope?.fileIds ? null : openView('importFiles')}
                />
              </Stack>

              <Stack gap={1}>
                <Typography sx={HELP_SX}>{BASE_POLICIES_HELP}</Typography>
                <BasePolicySection
                  control={control}
                  basePolicies={basePolicies}
                  savedBaseIds={savedBaseIds}
                  disabled={!generatesCatalog}
                  missingBase={missingBase}
                  onUseExistingPolicy={openView('useExistingPolicy')}
                  onCreateManagedPolicy={openView('createManagedPolicy')}
                />
              </Stack>

              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <BackButton disabled={loading} onClick={() => setStep(STATUS_STEP)}>
                  Back
                </BackButton>
                {/* With nothing to build, saving is the trust alone, which reads as such. */}
                {generatesCatalog ? (
                  <SuccessButton
                    type="submit"
                    loading={loading}
                    disabled={loading || !isValid || !artifactsReady || missingBase}>
                    Generate
                  </SuccessButton>
                ) : (
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={loading || !isValid || !artifactsReady}
                    sx={{ textTransform: 'none', color: '#D1D5DB', borderColor: '#6B7280' }}>
                    Save Trust Only
                  </Button>
                )}
              </Stack>
            </Stack>
          </form>
        )}
      </StatusDialog>

      <Win32FileSetModal
        open={open && view.kind === 'fileSet'}
        onClose={closeView}
        globalApplicationVersionId={globalApplicationVersionId}
        fileSetId={view.fileSetId}
      />
      <UncoveredFilesModal
        open={open && view.kind === 'importFiles'}
        onClose={closeView}
        globalApplicationVersionId={globalApplicationVersionId}
        scope={scope}
        title={importTitle}
      />
      <UseExistingPolicyModal
        open={open && view.kind === 'useExistingPolicy'}
        onDone={selectBasePolicy}
        onClose={closeView}
      />
      <CreateManagedBasePolicyModal
        open={open && view.kind === 'createManagedPolicy'}
        onDone={selectBasePolicy}
        onClose={closeView}
      />
    </>
  );
};

export default Win32AppStatusModal;
